import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Zap, Phone, Mail, MapPin, Globe, ArrowLeft, Package, Truck, PackageOpen, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { LEGACY_PIPELINE, StatusBadge, getLegacyPipelineIdx, POST_RECEIVED_PIPELINE, POST_RX_HISTORY, isPostReceived, getPostReceivedIdx, fmtPostReceived } from "@/components/StatusPipeline";
import BarcodePanel from "@/components/BarcodePanel";
import { buildTrackingUrl } from "@/lib/courier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDT = d => d ? new Date(d).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "";
const fmtD  = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"}) : "";

// 3-step inbound pipeline (Customer Sending Charger mode)
const INBOUND_PIPELINE = [
  { status:"Courier Initiated",         icon:Package,     emoji:"📦", dot:"#7c3aed", desc:"Customer has initiated a courier to send the charger" },
  { status:"In Transit",                icon:Truck,       emoji:"🚚", dot:"#2563eb", desc:"Charger is in transit to service center" },
  { status:"Received at Service Center",icon:PackageOpen, emoji:"🏪", dot:"#16a34a", desc:"Charger has arrived at the service center" },
];

// Maps any status → correct step in the 9-step LEGACY_PIPELINE
const STATUS_TO_STEP = {
  "Under Inspection":      "Received",
  "Waiting for Approval":  "Received",
  "Repair in Progress":    "Repairing",
  "Repaired":              "Repairing",
  "Replacement Approved":  "In Dispatch",
  "Replacement Dispatched":"In Dispatch",
  "Out for Delivery":      "In Dispatch",
  "Completed":             "In Transit",    // map to last step
  "Warranty Rejected":     "In Transit",    // map to last step
  "Delivered":             "In Transit",    // backward compat
  "In Repair":             "Repairing",
};

const getLegacyIdx = (status, history = []) => {
  const mapped = STATUS_TO_STEP[status];
  const resolved = mapped || status;
  return getLegacyPipelineIdx(resolved, history);
};

export default function TrackJob() {
  const [searchInput, setSearchInput] = useState("");
  const [job,    setJob]    = useState(null);
  const [sc,     setSC]     = useState(null);
  const [loading,setLoading]= useState(false);
  const [error,  setError]  = useState(null);

  const appUrl = window.location.origin;

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {});
    // Track page visit
    axios.post(`${API}/analytics/event`, { page: "track", referrer: document.referrer }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const jobNo  = params.get("job");
    if (jobNo) { setSearchInput(jobNo); doSearch(jobNo); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount; doSearch and API are stable references

  const doSearch = async (val) => {
    const q = (val || searchInput).trim();
    if (!q) return;
    setLoading(true); setError(null); setJob(null);
    try {
      const res = await axios.get(`${API}/jobs/${q}`);
      setJob(res.data);
    } catch { setError("Job not found. Please check the job number."); }
    finally  { setLoading(false); }
  };

  const handleSearch = e => { e.preventDefault(); doSearch(); };

  const currentIdx = job ? getLegacyIdx(job.current_status, job.status_history || []) : -1;
  const trackUrl   = job ? `${appUrl}/track?job=${job.job_no}` : "";

  // Map history entries by status for quick lookup
  const historyMap = {};
  if (job?.status_history) {
    job.status_history.forEach(h => { historyMap[h.status] = h; });
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg,#f0fdf4 0%,#ffffff 50%,#f0fdf4 100%)" }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt="XSTRONG POWER"
              className="h-12 w-auto object-contain"
            />
            <div>
              <p className="font-bold text-sm text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                {sc?.name || "XSTRONG POWER EV CHARGER"}
              </p>
              <p className="text-xs text-green-600 font-medium">{sc?.tagline || "Service Center"}</p>
            </div>
          </div>
          <Link to="/" className="text-xs text-slate-400 hover:text-green-700 flex items-center gap-1">
            <ArrowLeft size={12} /> Staff Portal
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero */}
        {!job && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Search size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Track Your Repair</h1>
            <p className="text-slate-500 text-sm">Enter your job number to see live pickup & repair status</p>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text" placeholder="Job number  e.g. XP-2026-0001"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value.toUpperCase())}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="track-job-search-input"
              />
            </div>
            <button type="submit" disabled={loading || !searchInput.trim()}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
              data-testid="track-job-search-button">
              {loading ? "..." : "Track"}
            </button>
          </form>
          {error && <p className="text-sm text-rose-600 mt-3">{error}</p>}
        </div>

        {job && (
          <div className="space-y-5">
            {/* Status Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Job Number</p>
                  <p className="font-mono text-2xl font-bold text-green-700">{job.job_no}</p>
                </div>
                <StatusBadge status={job.current_status} size="lg" />
              </div>
              <p className="text-xs text-slate-400">Received on {fmtD(job.created_at)}</p>
            </div>

            {/* Barcode */}
            <BarcodePanel jobNo={job.job_no} trackingUrl={trackUrl} />

            {/* Courier tracking — shown when courier details are added */}
            {job.courier_details?.awb_number && (
              <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg">🚚</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                      Courier Dispatched!
                    </p>
                    <p className="text-xs text-slate-500">Your device is on the way</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Courier Service</span>
                    <span className="text-sm font-semibold text-slate-800">{job.courier_details.courier_name || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-xs text-slate-500">AWB / Tracking No.</span>
                    <span className="text-sm font-mono font-bold text-blue-700 tracking-wide">{job.courier_details.awb_number}</span>
                  </div>
                </div>
                {(job.courier_details.tracking_url || buildTrackingUrl(job.courier_details.courier_name, job.courier_details.awb_number)) && (
                  <a
                    href={job.courier_details.tracking_url || buildTrackingUrl(job.courier_details.courier_name, job.courier_details.awb_number)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-sm transition-colors"
                    data-testid="courier-track-button"
                  >
                    🔗 Track Your Shipment on {job.courier_details.courier_name || "Courier"}
                  </a>
                )}
              </div>
            )}

            {/* XP Charger — outgoing shipment cards (New Charger On Its Way) */}
            {(job.products||[]).map((p, pi) => p.xp_charger_status && p.outgoing_courier?.awb_number ? (
              <div key={"out-"+pi} className="bg-white rounded-2xl border-2 border-green-200 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-base">🎁</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-green-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                      New Charger On Its Way to You
                    </p>
                    {(job.products||[]).length > 1 && <p className="text-xs text-green-600">Charger #{pi+1}</p>}
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  {p.outgoing_courier.courier_name && (
                    <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Courier</span>
                      <span className="text-sm font-semibold">{p.outgoing_courier.courier_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                    <span className="text-xs text-slate-500">AWB Number</span>
                    <span className="text-sm font-mono font-bold text-green-700">{p.outgoing_courier.awb_number}</span>
                  </div>
                  {p.outgoing_courier.dispatch_date && (
                    <div className="flex justify-between items-center py-1.5">
                      <span className="text-xs text-slate-500">Dispatch Date</span>
                      <span className="text-sm">{p.outgoing_courier.dispatch_date}</span>
                    </div>
                  )}
                </div>
                {p.outgoing_courier.tracking_url && (
                  <a href={p.outgoing_courier.tracking_url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors">
                    🔗 Track New Charger Shipment
                  </a>
                )}
              </div>
            ) : null)}

            {/* XP Charger — incoming return shipment cards */}
            {(job.products||[]).map((p, pi) => p.xp_charger_status && p.incoming_courier?.awb_number ? (
              <div key={"in-"+pi} className="bg-white rounded-2xl border-2 border-orange-200 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-base">🔄</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-orange-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                      Return Shipment
                    </p>
                    {(job.products||[]).length > 1 && <p className="text-xs text-orange-600">Charger #{pi+1} — original return</p>}
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  {p.incoming_courier.courier_name && (
                    <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Courier</span>
                      <span className="text-sm font-semibold">{p.incoming_courier.courier_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                    <span className="text-xs text-slate-500">AWB Number</span>
                    <span className="text-sm font-mono font-bold text-orange-700">{p.incoming_courier.awb_number}</span>
                  </div>
                  {p.incoming_courier.expected_return_date && (
                    <div className="flex justify-between items-center py-1.5">
                      <span className="text-xs text-slate-500">Expected Return</span>
                      <span className="text-sm">{p.incoming_courier.expected_return_date}</span>
                    </div>
                  )}
                </div>
                {p.incoming_courier.tracking_url && (
                  <a href={p.incoming_courier.tracking_url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors">
                    🔗 Track Return Shipment
                  </a>
                )}
              </div>
            ) : null)}

            {/* Service Journey — Inbound / Post-Received / Standard */}
            {job.service_mode === "customer_sending" ? (
              /* ── INBOUND 3-STEP TIMELINE ── */
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                  Inbound Shipment Tracking
                </h3>
                <p className="text-xs text-slate-500 mb-4">We're waiting for your charger to arrive at our service center.</p>
                {job.inbound_courier?.awb_number && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-5">
                    <p className="text-xs font-bold text-orange-800 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Truck size={12}/> Your Inbound Shipment</p>
                    <div className="space-y-1.5">
                      {job.inbound_courier.courier_name && (
                        <div className="flex justify-between items-center text-sm"><span className="text-slate-500 text-xs">Courier</span><span className="font-semibold text-slate-800">{job.inbound_courier.courier_name}</span></div>
                      )}
                      <div className="flex justify-between items-center text-sm"><span className="text-slate-500 text-xs">AWB Number</span><span className="font-mono font-bold text-orange-700">{job.inbound_courier.awb_number}</span></div>
                    </div>
                    {job.inbound_courier.tracking_url && (
                      <a href={job.inbound_courier.tracking_url} target="_blank" rel="noreferrer"
                        className="flex items-center justify-center gap-2 w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors">
                        <ExternalLink size={14}/> Track Your Shipment
                      </a>
                    )}
                  </div>
                )}
                <div className="mb-5">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Courier Initiated</span>
                    <span>{Math.round(((INBOUND_PIPELINE.findIndex(s=>s.status===job.inbound_status)+1)/INBOUND_PIPELINE.length)*100)}% complete</span>
                    <span>Received</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full transition-all duration-700"
                      style={{width:`${((INBOUND_PIPELINE.findIndex(s=>s.status===job.inbound_status)+1)/INBOUND_PIPELINE.length)*100}%`}}/>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100"/>
                  <div className="space-y-0">
                    {INBOUND_PIPELINE.map((step,i)=>{
                      const inboundIdx=INBOUND_PIPELINE.findIndex(s=>s.status===job.inbound_status);
                      const done=i<inboundIdx,current=i===inboundIdx,future=i>inboundIdx,Icon=step.icon;
                      return (
                        <div key={step.status} className={`relative flex gap-4 pb-6 last:pb-0 ${future?"opacity-40":""}`}>
                          <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${current?"border-orange-400 bg-orange-50 shadow-md shadow-orange-100":done?"border-orange-300 bg-orange-50":"border-slate-200 bg-white"}`}>
                            {current?<div className="relative"><div className="absolute inset-0 rounded-full animate-ping" style={{background:step.dot,opacity:0.25}}/><Icon size={16} style={{color:step.dot}} className="relative z-10"/></div>:<Icon size={16} className={done?"text-orange-600":"text-slate-300"}/>}
                          </div>
                          <div className="flex-1 min-w-0 pt-1.5">
                            <div className="flex items-center gap-2"><span className="text-base">{step.emoji}</span><span className={`text-sm font-semibold ${current?"text-orange-700":done?"text-slate-800":"text-slate-400"}`}>{step.status}</span>{current&&<span className="text-xs bg-orange-100 text-orange-700 font-medium px-2 py-0.5 rounded-full">Current</span>}</div>
                            <p className={`text-xs mt-0.5 ${done||current?"text-slate-500":"text-slate-300"}`}>{step.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : isPostReceived(job.current_status, job.status_history || []) ? (
              /* ── POST-RECEIVED 7-STEP PIPELINE ── */
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-2" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                  Service Journey
                </h3>
                <p className="text-xs text-slate-500 mb-4">Track your device through our service process</p>
                {/* Progress */}
                {(() => {
                  const idx = getPostReceivedIdx(job.current_status);
                  const histByStage = {};
                  POST_RECEIVED_PIPELINE.forEach(step => {
                    const aliases = POST_RX_HISTORY[step.status] || [step.status];
                    const entry = (job.status_history||[]).find(h => aliases.includes(h.status));
                    if (entry) histByStage[step.status] = entry;
                  });
                  return (
                    <>
                      <div className="mb-5">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Received</span>
                          <span>{Math.round(((idx+1)/POST_RECEIVED_PIPELINE.length)*100)}% complete</span>
                          <span>Completed</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all duration-700"
                            style={{ width:`${((idx+1)/POST_RECEIVED_PIPELINE.length)*100}%` }}/>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100"/>
                        <div className="space-y-0">
                          {POST_RECEIVED_PIPELINE.map((step, i) => {
                            const done    = i < idx;
                            const current = i === idx;
                            const future  = i > idx;
                            const hist    = histByStage[step.status];
                            const Icon    = step.icon;
                            return (
                              <div key={step.status} className={`relative flex gap-4 pb-6 last:pb-0 ${future?"opacity-40":""}`}>
                                <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                                  current ? "border-green-500 bg-green-50 shadow-md shadow-green-100" : done ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"
                                }`}>
                                  {current
                                    ? <div className="relative"><div className="absolute inset-0 rounded-full animate-ping" style={{background:step.dot,opacity:0.25}}/><Icon size={16} style={{color:step.dot}} className="relative z-10"/></div>
                                    : <Icon size={16} className={done?"text-green-600":"text-slate-300"}/>}
                                </div>
                                <div className="flex-1 min-w-0 pt-1.5">
                                  <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-base">{step.emoji}</span>
                                      <span className={`text-sm font-semibold ${current?"text-green-700":done?"text-slate-800":"text-slate-400"}`}>{step.status}</span>
                                      {current && <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">Current</span>}
                                    </div>
                                    {hist && (
                                      <span className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100 whitespace-nowrap">
                                        {fmtPostReceived(hist.timestamp)}
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-xs mt-0.5 ${done||current?"text-slate-500":"text-slate-300"}`}>{step.desc}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              /* ── STANDARD 9-STEP PIPELINE (pre-received pickup journey) ── */
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-5" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                  Service Journey — Step {currentIdx + 1} of {LEGACY_PIPELINE.length}
                </h3>

                {/* Progress bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Pick Up Request</span>
                    <span>{Math.round(((currentIdx + 1) / LEGACY_PIPELINE.length) * 100)}% complete</span>
                    <span>In Transit</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all duration-700"
                      style={{ width: `${((currentIdx + 1) / LEGACY_PIPELINE.length) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Vertical step list */}
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100" />

                  <div className="space-y-0">
                    {LEGACY_PIPELINE.map((step, i) => {
                      const done    = i < currentIdx;
                      const current = i === currentIdx;
                      const future  = i > currentIdx;
                      const hist    = historyMap[step.status];
                      const Icon    = step.icon;

                      return (
                        <div key={step.status} className={`relative flex gap-4 pb-6 last:pb-0 ${
                          future ? "opacity-40" : ""
                        }`}>
                          {/* Icon circle */}
                          <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                            current
                              ? `border-green-500 bg-green-50 shadow-md shadow-green-100`
                              : done
                              ? `border-green-300 bg-green-50`
                              : `border-slate-200 bg-white`
                          }`}>
                            {current ? (
                              <div className="relative">
                                <div className="absolute inset-0 rounded-full animate-ping" style={{ background: step.dot, opacity: 0.25 }} />
                                <Icon size={16} style={{ color: step.dot }} className="relative z-10" />
                              </div>
                            ) : (
                              <Icon size={16} className={done ? "text-green-600" : "text-slate-300"} />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 pt-1.5">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="text-base">{step.emoji}</span>
                                <span className={`text-sm font-semibold ${
                                  current ? "text-green-700" : done ? "text-slate-800" : "text-slate-400"
                                }`}>{step.status}</span>
                                {current && (
                                  <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
                                    Current
                                  </span>
                                )}
                              </div>
                            {hist && (
                              <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDT(hist.timestamp)}</span>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 ${ done || current ? "text-slate-500" : "text-slate-300" }`}>
                            {step.desc}
                          </p>
                          {hist?.notes && (
                            <p className="text-xs mt-1 text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                              📝 {hist.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            {/* Job Info */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Your Chargers</h3>
                <span className="text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">
                  {(job.products||[]).length} unit{(job.products||[]).length!==1?"s":""}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><p className="text-xs text-slate-400 mb-0.5">Customer</p><p className="text-sm font-medium text-slate-900">{job.customer?.name}</p></div>
                <div><p className="text-xs text-slate-400 mb-0.5">Date Received</p><p className="text-sm text-slate-700">{fmtD(job.created_at)}</p></div>
                {job.customer?.phone && <div><p className="text-xs text-slate-400 mb-0.5">Phone</p><p className="text-sm text-slate-700">{job.customer.phone}{job.customer?.alt_phone ? ` / ${job.customer.alt_phone}` : ""}</p></div>}
                {(job.customer?.city || job.customer?.state) && <div><p className="text-xs text-slate-400 mb-0.5">Location</p><p className="text-sm text-slate-700">{[job.customer?.city, job.customer?.state, job.customer?.pincode].filter(Boolean).join(", ")}</p></div>}
              </div>
              {(job.products||[]).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-slate-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {["#","Battery Cut-off / Amp","Serial #","Purchase Date","Body"].map(h=>(
                          <th key={h} className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(job.products||[]).map((p,i)=>(
                        <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                          <td className="px-3 py-2 text-slate-400">{i+1}</td>
                          <td className="px-3 py-2 font-mono font-semibold text-green-700">{p.charger_spec||"—"}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{p.serial_number||"—"}</td>
                          <td className="px-3 py-2 text-slate-600">{p.purchase_date||"—"}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                              p.body_type==="Metal Body" ? "bg-slate-100 text-slate-700" :
                              p.body_type==="Aluminium Body" ? "bg-sky-50 text-sky-700" : "text-slate-400"
                            }`}>{p.body_type||"—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {job.specs?.fault_description && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Reported Issue</p>
                  <p className="text-sm text-slate-700">{job.specs.fault_description}</p>
                </div>
              )}
            </div>

            {/* Confirm info */}
            <div className="bg-green-50 rounded-2xl border border-green-200 p-4">
              <p className="text-xs font-semibold text-green-800 mb-1">Please verify your details</p>
              <p className="text-xs text-green-700">
                If any information above is incorrect or you have queries, please contact our service center immediately with your job number <strong>{job.job_no}</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Service Center Contact */}
        {sc && (sc.phone || sc.email || sc.address) && (
          <div className="mt-8 bg-white rounded-2xl border border-green-200/60 p-5">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">Contact Service Center</p>
            <p className="font-semibold text-sm text-slate-900 mb-3" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{sc.name}</p>
            <div className="space-y-2">
              {sc.phone && (
                <a href={`tel:${sc.phone}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-green-700">
                  <Phone size={13} className="text-green-600" /> {sc.phone}{sc.phone2 ? ` / ${sc.phone2}` : ""}
                </a>
              )}
              {sc.email && (
                <a href={`mailto:${sc.email}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-green-700">
                  <Mail size={13} className="text-green-600" /> {sc.email}
                </a>
              )}
              {sc.website && (
                <a href={sc.website.startsWith("http") ? sc.website : `https://${sc.website}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-700 hover:text-green-700">
                  <Globe size={13} className="text-green-600" /> {sc.website}
                </a>
              )}
              {(sc.address || sc.city) && (
                <div className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin size={13} className="text-green-600 mt-0.5 flex-shrink-0" />
                  {[sc.address, sc.city, sc.state, sc.pincode].filter(Boolean).join(", ")}
                </div>
              )}
              {sc.working_hours && <p className="text-xs text-slate-400">{sc.working_hours}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
